/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useColumnPreferences.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom hook for managing column preferences, visibility, and ordering
    with persistence and user-specific customization for data grids.

 🔧 Notes:
    - Implements column preference management with persistence
    - Supports column visibility, ordering, and width customization
    - Handles user-specific column configurations
    - Integrates with API for preference storage and retrieval
    - Provides drag-and-drop column reordering functionality
    - Supports multi-tenant column preference management

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { buildApiUrl, getApiHeaders } from '../config/api';
import { useAuthStore } from '../store/auth';

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  render?: (value: unknown, item: unknown) => ReactNode;
}

interface ColumnConfigs {
  [moduleName: string]: ColumnConfig[];
}

interface UseColumnPreferencesProps {
  moduleName: string;
  defaultColumns: ColumnConfig[];
}

export function useColumnPreferences({ moduleName, defaultColumns }: UseColumnPreferencesProps) {
  const { user } = useAuthStore();
  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const defaultColumnsRef = useRef(defaultColumns);
  const moduleNameRef = useRef(moduleName);
  const userIdRef = useRef(user?.id);
  
  // Track if we've loaded preferences to prevent duplicate API calls
  const loadedKeyRef = useRef<string | null>(null);
  
  // Update refs when values change
  useEffect(() => {
    defaultColumnsRef.current = defaultColumns;
    moduleNameRef.current = moduleName;
    userIdRef.current = user?.id;
  }, [defaultColumns, moduleName, user?.id]);

  // Re-apply translated labels whenever defaultColumns changes (e.g. on language switch)
  // This ensures the visible column headers update even when preferences are loaded from API.
  useEffect(() => {
    setColumns(prev => {
      let hasChanges = false;
      const updated = prev.map(col => {
        const defaultCol = defaultColumns.find(d => d.key === col.key);
        if (defaultCol && col.label !== defaultCol.label) {
          hasChanges = true;
          return { ...col, label: defaultCol.label };
        }
        return col;
      });
      return hasChanges ? updated : prev;
    });
  }, [defaultColumns]);

  // Load column preferences from API
  const loadColumnPreferences = useCallback(async () => {
    try {
      const currentModuleName = moduleNameRef.current;
      const currentUserId = userIdRef.current;
      
      // Create a unique key for this load request
      const loadKey = `${currentModuleName}-${currentUserId}`;
      
      // Prevent duplicate calls for the same module/user combination
      if (loadedKeyRef.current === loadKey) {
        return;
      }
      loadedKeyRef.current = loadKey;
      
      setIsLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl('/api/preferences/me/column-configs'), {
        method: 'GET',
        headers: getApiHeaders('main', currentUserId)
      });

      if (!response.ok) {
        throw new Error(`Failed to load column preferences: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.data[currentModuleName]) {
        // Merge saved preferences with default columns to ensure all columns are present
        const savedColumns = result.data[currentModuleName];
        const currentDefaultColumns = defaultColumnsRef.current;
        const mergedColumns = currentDefaultColumns.map(defaultCol => {
          const savedCol = savedColumns.find((saved: ColumnConfig) => saved.key === defaultCol.key);
          return savedCol ? { 
            ...defaultCol, 
            ...savedCol, 
            label: defaultCol.label // Preserve the translated label from frontend
          } : defaultCol;
        });
        
        setColumns(mergedColumns);
      } else {
        // No saved preferences, use defaults
        setColumns(defaultColumnsRef.current);
      }
    } catch (err) {
      console.error('Error loading column preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to load column preferences');
      // Fallback to default columns
      setColumns(defaultColumnsRef.current); // Use ref to avoid dependency issues
      loadedKeyRef.current = null; // Reset on error to allow retry
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array since we use refs

  // Save column preferences to API
  const saveColumnPreferences = useCallback(async (newColumns: ColumnConfig[]) => {
    try {
      setError(null);

      const response = await fetch(buildApiUrl(`/api/preferences/me/column-configs/${moduleName}`), {
        method: 'PUT',
        headers: {
          ...getApiHeaders('main', user?.id),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ columns: newColumns })
      });

      if (!response.ok) {
        throw new Error(`Failed to save column preferences: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setColumns(newColumns);
      } else {
        throw new Error(result.error || 'Failed to save column preferences');
      }
    } catch (err) {
      console.error('Error saving column preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to save column preferences');
      throw err; // Re-throw so the UI can handle the error
    }
  }, [moduleName]);

  // Reset column preferences to defaults
  const resetColumnPreferences = useCallback(async () => {
    try {
      setError(null);

      const response = await fetch(buildApiUrl(`/api/preferences/me/column-configs/${moduleName}`), {
        method: 'DELETE',
        headers: getApiHeaders('main', user?.id)
      });

      if (!response.ok) {
        throw new Error(`Failed to reset column preferences: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setColumns(defaultColumns);
      } else {
        throw new Error(result.error || 'Failed to reset column preferences');
      }
    } catch (err) {
      console.error('Error resetting column preferences:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset column preferences');
      throw err; // Re-throw so the UI can handle the error
    }
  }, [moduleName, defaultColumns]);

  // Toggle column visibility
  const toggleColumn = useCallback((columnKey: string) => {
    setColumns(currentColumns => {
      const newColumns = currentColumns.map(col => 
        col.key === columnKey ? { ...col, visible: !col.visible } : col
      );
      
      // Save to API
      saveColumnPreferences(newColumns).catch(err => {
        console.error('Failed to save column toggle:', err);
        // Revert on error - use the current columns from the closure
        setColumns(currentColumns);
      });
      
      return newColumns;
    });
  }, [saveColumnPreferences]);

  // Load preferences when moduleName or user changes
  useEffect(() => {
    // Reset the loaded key when module or user changes to allow new load
    const newLoadKey = `${moduleName}-${user?.id}`;
    if (loadedKeyRef.current !== newLoadKey) {
      loadedKeyRef.current = null;
      loadColumnPreferences();
    }
  }, [moduleName, user?.id, loadColumnPreferences]);

  return {
    columns,
    isLoading,
    error,
    toggleColumn,
    saveColumnPreferences,
    resetColumnPreferences,
    loadColumnPreferences
  };
}
